import { Alert, Button } from "@digdir/designsystemet-react";
import { ChevronLeftIcon, ChevronRightIcon } from "@navikt/aksel-icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { BestillingPageKey } from "@/features/bestillinger";
import { cn } from "@/utils";
import { useBestillingNavigation } from "../hooks";

export type BestillingNavigationActionsProps = {
  className?: string;
  currentPageKey: BestillingPageKey;
  disableNext?: boolean;
  isLoading?: boolean;
  nextButtonTextKey?: string;
  onPreviousCallback?: () => Promise<void>;
};

export function BestillingNavigationActions({
  className,
  currentPageKey,
  disableNext,
  isLoading,
  nextButtonTextKey = "button.next",
  onPreviousCallback = () => Promise.resolve(),
}: BestillingNavigationActionsProps) {
  const [showErrorOnNextButtonClick, setShowErrorOnNextButtonClick] =
    useState(false);
  const { t } = useTranslation();
  const { navigatePrevious, isFirstStep: isFirstPage } =
    useBestillingNavigation(currentPageKey);

  function handlePreviousClick() {
    onPreviousCallback()
      .then(navigatePrevious)
      .catch((error) => {
        console.error("Error in onPreviousCallback:", error);
      });
  }

  const handleNextClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disableNext) {
      event?.preventDefault();
      event?.stopPropagation();
      setShowErrorOnNextButtonClick(true);
      return;
    }
    setShowErrorOnNextButtonClick(false);
  };

  return (
    <>
      {showErrorOnNextButtonClick && (
        <Alert data-color="warning" className="mb-4 w-fit">
          {t("skjema.navigering.next-button-disabled")}
        </Alert>
      )}
      <div className={cn("flex items-center gap-6", className)}>
        <Button
          className={cn({
            hidden: isFirstPage,
          })}
          variant="secondary"
          type="button"
          onClick={handlePreviousClick}
          disabled={isFirstPage}
        >
          <ChevronLeftIcon />
          <span>{t("button.back")}</span>
        </Button>
        <Button type="submit" loading={isLoading} onClick={handleNextClick}>
          <span>{t(nextButtonTextKey)}</span>
          <ChevronRightIcon fontSize="1.5rem" className="shrink-0" />
        </Button>
      </div>
    </>
  );
}
